import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import apiClient from '../../api/axios';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';

const CATEGORY_MAP = {
    minggu: {
        title: 'TATA IBADAH MINGGU',
        endpoint: 'ti_minggu',
        color: 'border-blue-500'
    },
    keluarga: {
        title: 'TATA IBADAH KELUARGA',
        endpoint: 'ti_ika',
        color: 'border-blue-500'
    },
    pelkat: {
        title: 'TATA IBADAH PELKAT',
        color: 'border-blue-500',
        subCategories: {
            pa: { title: 'Persekutuan Anak (PA)', endpoint: 'ti_pelkat' },
            pt: { title: 'Persekutuan Teruna (PT)', endpoint: 'ti_pelkat' },
            gp: { title: 'Gerakan Pemuda (GP)', endpoint: 'ti_pelkat' },
            pkp: { title: 'Persekutuan Kaum Perempuan (PKP)', endpoint: 'ti_pelkat' },
            pkb: { title: 'Persekutuan Kaum Bapak (PKB)', endpoint: 'ti_pelkat' },
            pklu: { title: 'Persekutuan Kaum Lanjut Usia (PKLU)', endpoint: 'ti_pelkat' },
        }
    }
};

const TataIbadahDetails = () => {
    const { type, subType, id } = useParams();
    // const navigate = useNavigate();

    const [ibadah, setIbadah] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [isInvalidType, setIsInvalidType] = useState(false);

    // Memproses Config berdasarkan Type dan SubType
    const categoryConfig = useMemo(() => {
        const main = CATEGORY_MAP[type?.toLowerCase()]; // Gunakan toLowerCase()
        if (!main) return null;

        if (type === 'pelkat') {
            const sub = main.subCategories[subType?.toLowerCase()]; // Gunakan toLowerCase()
            return sub ? { ...sub, mainTitle: main.title, borderColor: main.color } : null;
        }
        return { ...main, mainTitle: main.title, borderColor: main.color };
    }, [type, subType]);

    useEffect(() => {
        if (!categoryConfig) {
            setIsInvalidType(true);
            return;
        }

        let isCancelled = false;
        const fetchIbadahDetails = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Memastikan endpoint benar
                const endpoint = type === 'pelkat'
                    ? `/ti_pelkat/detail/${id}`
                    : `/${categoryConfig.endpoint}/detail/${id}`;

                const response = await apiClient.get(endpoint);
                const resBody = response.data?.data;

                if (!isCancelled) {
                    if (resBody) {
                        // Cek jika data dibungkus dalam array (kasus paginasi yang salah sasaran)
                        if (resBody.data && Array.isArray(resBody.data)) {
                            setIbadah(resBody.data[0]);
                        } else {
                            setIbadah(resBody);
                        }
                    } else {
                        setError('Data tidak ditemukan.');
                    }
                }
            } catch (err) {
                if (!isCancelled) {
                    setError('Gagal mengambil detail tata ibadah.');
                }
            } finally {
                if (!isCancelled) {
                    setProgress(100);
                    setTimeout(() => setIsLoading(false), 700);
                }
            }
        };

        fetchIbadahDetails();
        return () => { isCancelled = true; };
    }, [id, type, categoryConfig]);

    if (isInvalidType) return <Navigate to="/tata-ibadah" replace />;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <LoadingSpinner progress={progress} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 text-center">
                <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
                    <div className="text-red-500 mb-4">
                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p className="text-gray-800 font-bold text-xl mb-4">{error}</p>
                    <Link to="/tata-ibadah" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition">
                        Kembali ke Daftar
                    </Link>
                </div>
            </div>
        );
    }

    const downloadFileName = `${ibadah?.title?.replace(/[^a-z0-9]/gi, '_') || 'Tata_Ibadah'}.pdf`;
    const ibadahDate = ibadah.created_at.split(' ')[0];

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <PageTransitionWrapper>
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">

                        {/* Judul Kategori */}
                        <div className="text-center mb-8">
                            <h1 className={`text-4xl sm:text-5xl font-extrabold border-b-4 ${categoryConfig.borderColor} pb-4 tracking-tight text-blue-900`}>
                                {categoryConfig.mainTitle}
                            </h1>
                            {type === 'pelkat' && (
                                <div className="mt-4">
                                    <span className="px-4 py-1 bg-yellow-100 text-yellow-800 rounded-full font-bold text-sm uppercase">
                                        Sub-Kategori: {categoryConfig.title}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Detail Info & Action Buttons */}
                        <div className="flex flex-col md:flex-row md:items-start justify-between mb-8 border-b pb-6 border-gray-100">
                            <div className="text-left mb-6 md:mb-0">
                                <h2 className="text-3xl font-bold text-gray-800 leading-snug">
                                    {ibadah?.title}
                                </h2>
                                <div className="flex items-center mt-3 text-gray-600">
                                    <span className="font-medium">Tanggal Rilis: </span>
                                    <span className="ml-1 text-blue-600 font-semibold">{ibadahDate}</span>
                                </div>
                            </div>

                            <div className="flex flex-row space-x-3 flex-shrink-0">
                                <a
                                    href={ibadah?.file}
                                    download={downloadFileName}
                                    className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-6 rounded-xl shadow-md transition-all active:scale-95"
                                >
                                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    Download
                                </a>

                                <Link
                                    to="/tata-ibadah"
                                    className="inline-flex items-center justify-center bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition-all duration-300 text-base"
                                >
                                    &larr; Kembali
                                </Link>
                            </div>
                        </div>

                        {/* PDF Viewer */}
                        {ibadah?.file ? (
                            <div className="overflow-hidden rounded-xl shadow-2xl bg-gray-200">
                                <iframe
                                    src={`${ibadah.file}#toolbar=0`}
                                    width="100%"
                                    height="750"
                                    title={ibadah.title}
                                    className="border-0"
                                    style={{ aspectRatio: '1 / 1.414' }}
                                >
                                    <p>Browser Anda tidak mendukung preview. Silakan <a href={ibadah.file} className="text-blue-600 underline">klik di sini</a> untuk melihat file.</p>
                                </iframe>
                            </div>
                        ) : (
                            <div className="p-10 bg-blue-50 border-2 border-dashed border-blue-200 text-blue-800 rounded-2xl text-center">
                                <p className="font-semibold text-lg italic">File PDF belum tersedia untuk tata ibadah ini.</p>
                            </div>
                        )}
                    </div>
                </div>
            </PageTransitionWrapper>
        </div>
    );
};

export default TataIbadahDetails;